/** Wraps LandingNewNav with org-admin drawer integration. */

import { useContext, useEffect, useMemo } from 'react'
import type { ReactNode } from 'react'
import { Platform, useWindowDimensions } from 'react-native'
import { usePathname, useRouter, type Href } from 'expo-router'
import {
  AccountAdminSiteMenuProvider,
  useAccountAdminSiteMenuOptional,
} from '../../../src/accounts/contexts/AccountAdminSiteMenuContext'
import { useAccountAdminNavDrawerOpen } from '../../../src/accounts/contexts/AccountAdminNavDrawerOpenContext'
import { useRegisterAccountAdminWebDrawerSiteLinks } from '../../../src/accounts/contexts/AccountAdminWebDrawerSiteLinksContext'
import { useAccountAdminSiteMenuLinks } from '../../../src/accounts/hooks/useAccountAdminSiteMenuLinks'
import {
  ACCOUNT_ADMIN_NAV_ITEMS,
  ACCOUNT_ADMIN_PUBLIC_ROUTE,
} from '../../../src/accounts/constants/accountAdmin.constants'
import { ACCOUNT_ADMIN_SIDEBAR_BREAKPOINT_PX } from '../../../src/accounts/constants/accountAdminNav.constants'
import {
  buildAccountAdminNavPath,
  getAccountAdminNavLabel,
} from '../../../src/accounts/helpers/accountAdminNav.helper'
import { useSupabaseAuth } from '../../../src/shared/contexts/SupabaseAuthContext'
import { LanguageContext } from '../../../src/i18n'
import { LANDING_SITE_NAV_ROUTES } from '../../../src/landing/constants/landingSiteNav.constants'
import {
  LandingNewNav,
  type LandingNavItem,
  type OrganizationMenuItem,
} from '../LandingNewNav/LandingNewNav'

interface LandingOrgAdminNewNavProps {
  onPressHome: () => void
  onPressContact: () => void
  onPressFeatures?: () => void
  onPressHowItWorks?: () => void
  onPressPricing?: () => void
  navMode?: 'marketing' | 'accountAdmin'
  headerRight?: ReactNode
}

function useOrganizationsHubMenuItem(
  router: ReturnType<typeof useRouter>,
  isSignedIn: boolean | null | undefined,
): OrganizationMenuItem[] {
  const languageContext = useContext(LanguageContext)
  if (!languageContext) {
    throw new Error('LandingOrgAdminNewNav must be used within LanguageProvider')
  }
  const { t } = languageContext
  return useMemo(
    () => [
      {
        key: 'organizations_hub',
        label: t.siteNav.organizations,
        description: t.features.organizations.description,
        onPress: () =>
          router.push(
            (isSignedIn ? ACCOUNT_ADMIN_PUBLIC_ROUTE : LANDING_SITE_NAV_ROUTES.ORGANIZATIONS) as Href,
          ),
      },
    ],
    [isSignedIn, router, t.siteNav.organizations, t.features.organizations.description],
  )
}

function useAccountAdminPrimaryNavItems(router: ReturnType<typeof useRouter>): LandingNavItem[] {
  return useMemo(
    () =>
      ACCOUNT_ADMIN_NAV_ITEMS.map((id) => ({
        key: id,
        label: getAccountAdminNavLabel(id),
        onPress: () => router.push(buildAccountAdminNavPath(id) as Href),
      })),
    [router],
  )
}

function LandingOrgAdminNewNavNative(props: LandingOrgAdminNewNavProps) {
  const pathname = usePathname() ?? ''
  const router = useRouter()
  const { isSignedIn } = useSupabaseAuth()
  const organizationHubMenuItems = useOrganizationsHubMenuItem(router, isSignedIn)
  const accountAdminPrimaryNavItems = useAccountAdminPrimaryNavItems(router)
  const useAccountAdminNav =
    props.navMode === 'accountAdmin' || pathname === '/account-admin' || pathname.startsWith('/account-admin/')

  return (
    <LandingNewNav
      onPressHome={props.onPressHome}
      onPressFeatures={props.onPressFeatures}
      onPressHowItWorks={props.onPressHowItWorks}
      onPressPricing={props.onPressPricing}
      onPressContact={props.onPressContact}
      primaryNavItems={useAccountAdminNav ? accountAdminPrimaryNavItems : undefined}
      organizationMenuItems={useAccountAdminNav ? undefined : organizationHubMenuItems}
      headerRight={props.headerRight}
    />
  )
}

export function LandingOrgAdminNewNav(props: LandingOrgAdminNewNavProps) {
  if (Platform.OS !== 'web') {
    return <LandingOrgAdminNewNavNative {...props} />
  }
  return (
    <AccountAdminSiteMenuProvider>
      <LandingOrgAdminNewNavWeb {...props} />
    </AccountAdminSiteMenuProvider>
  )
}

function LandingOrgAdminNewNavWeb({
  onPressHome,
  onPressContact,
  onPressFeatures,
  onPressHowItWorks,
  onPressPricing,
  navMode = 'marketing',
  headerRight,
}: LandingOrgAdminNewNavProps) {
  const { width: windowWidth } = useWindowDimensions()
  const pathname = usePathname() ?? ''
  const router = useRouter()
  const siteMenuLinks = useAccountAdminSiteMenuLinks({
    onHome: onPressHome,
    onContact: onPressContact,
    onPacks: onPressPricing,
  })
  useRegisterAccountAdminWebDrawerSiteLinks(siteMenuLinks)
  const { setDrawerOpen } = useAccountAdminNavDrawerOpen()
  const siteMenuBridge = useAccountAdminSiteMenuOptional()
  const { isSignedIn } = useSupabaseAuth()
  const organizationHubMenuItems = useOrganizationsHubMenuItem(router, isSignedIn)
  const accountAdminPrimaryNavItems = useAccountAdminPrimaryNavItems(router)
  const useAccountAdminNav =
    navMode === 'accountAdmin' || pathname === '/account-admin' || pathname.startsWith('/account-admin/')
  /** Match `AccountAdminScreen` — wide site surface uses sidebar; header should not duplicate those tabs. */
  const centerNavEmpty =
    useAccountAdminNav && windowWidth >= ACCOUNT_ADMIN_SIDEBAR_BREAKPOINT_PX

  useEffect(() => {
    if (!siteMenuBridge) return undefined
    siteMenuBridge.registerOpenMenu(() => setDrawerOpen(true))
    return () => {
      siteMenuBridge.registerOpenMenu(null)
    }
  }, [siteMenuBridge, setDrawerOpen])

  return (
    <LandingNewNav
      onPressHome={onPressHome}
      onPressFeatures={onPressFeatures}
      onPressHowItWorks={onPressHowItWorks}
      onPressPricing={onPressPricing}
      onPressContact={onPressContact}
      centerNavEmpty={centerNavEmpty}
      primaryNavItems={useAccountAdminNav ? accountAdminPrimaryNavItems : undefined}
      organizationMenuItems={useAccountAdminNav ? undefined : organizationHubMenuItems}
      headerRight={headerRight}
    />
  )
}
